/**
 * Snapshot of Analytics Hub chart data for AI chat (market_context.analytics_hub_context).
 * Updated when RTL / Stable charts load — companion reads via App aiMarketContext.
 */

import type { RtlContractSeries, RtlSeriesMetric } from '../hooks/useRtlTimeSeries';
import type { ContractHistoricalParams } from '../types/contractHistorical';
import {
  formatMarketDateTime,
  formatMarketTimeShort,
  getEasternSessionYmdFromMs,
  parseQuestDbTimestampMs,
} from './marketTimeUtils';

const MAX_SAMPLE_POINTS = 36;

export interface RtlContractAiSeries {
  label: string;
  strike: number;
  expiry: string;
  side: 'C' | 'P';
  point_count: number;
  min: number;
  max: number;
  last: number;
  trend: 'up' | 'down' | 'flat';
  time_range_et: { start: string; end: string };
  samples: Array<{ time_et: string; value: number }>;
}

export interface RtlChartAiContext {
  symbol: string;
  metric: RtlSeriesMetric;
  session_ymd: string;
  available_sessions: string[];
  contracts: RtlContractAiSeries[];
  loading?: boolean;
}

export interface StableChartAiContext {
  contract_label: string;
  underlying: string;
  strike: number;
  expiry: string;
  option_type: string;
  active_tab: 'eod' | 'intraday';
  eod?: {
    date_from: string;
    date_to: string;
    primary_metric: string;
    observations: number;
    min: number;
    max: number;
    last: number;
    samples: Array<{ date: string; value: number }>;
  };
  intraday?: {
    session_ymd: string;
    trade_count: number;
    net_signed_usd: number;
    samples: Array<{ time_et: string; cumulative_usd: number }>;
  };
  loading?: boolean;
}

export interface AnalyticsHubAiSnapshot {
  updated_at: string;
  view: 'rtl' | 'stable' | null;
  rtl?: RtlChartAiContext | null;
  stable?: StableChartAiContext | null;
}

type Listener = () => void;

let snapshot: AnalyticsHubAiSnapshot | null = null;
const listeners = new Set<Listener>();

export function getAnalyticsHubAiSnapshot(): AnalyticsHubAiSnapshot | null {
  return snapshot;
}

export function subscribeAnalyticsHubAiSnapshot(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function setAnalyticsHubAiSnapshot(next: AnalyticsHubAiSnapshot | null): void {
  snapshot = next;
  listeners.forEach((cb) => cb());
}

export function patchAnalyticsHubAiSnapshot(
  patch: Partial<AnalyticsHubAiSnapshot> & { view?: 'rtl' | 'stable' | null },
): void {
  const prev = snapshot ?? { updated_at: '', view: null };
  setAnalyticsHubAiSnapshot({
    ...prev,
    ...patch,
    updated_at: new Date().toISOString(),
  });
}

function downsample<T>(items: T[], max: number): T[] {
  if (items.length <= max) return items;
  const step = items.length / max;
  const out: T[] = [];
  for (let i = 0; i < max; i += 1) {
    out.push(items[Math.min(items.length - 1, Math.floor(i * step))]);
  }
  return out;
}

function seriesTrend(values: number[]): 'up' | 'down' | 'flat' {
  if (values.length < 3) return 'flat';
  const third = Math.max(1, Math.floor(values.length / 3));
  const start = values.slice(0, third);
  const end = values.slice(-third);
  const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const delta = avg(end) - avg(start);
  const scale = Math.max(Math.abs(avg(start)), Math.abs(avg(end)), 1);
  if (delta / scale > 0.05) return 'up';
  if (delta / scale < -0.05) return 'down';
  return 'flat';
}

export function buildRtlChartAiContext(opts: {
  symbol: string;
  metric: RtlSeriesMetric;
  sessionYmd: string;
  chartSeries: RtlContractSeries[];
  availableSessions: string[];
  loading?: boolean;
}): RtlChartAiContext | null {
  const { symbol, metric, sessionYmd, chartSeries, availableSessions, loading } = opts;
  if (!chartSeries.length) return null;

  const contracts: RtlContractAiSeries[] = chartSeries.map((s) => {
    const pts = sessionYmd
      ? s.points.filter((p) => getEasternSessionYmdFromMs(p.tsMs) === sessionYmd)
      : s.points;
    const values = pts.map((p) => p.value);
    const sorted = [...pts].sort((a, b) => a.tsMs - b.tsMs);
    const samples = downsample(sorted, MAX_SAMPLE_POINTS).map((p) => ({
      time_et: formatMarketTimeShort(p.tsMs),
      value: p.value,
    }));
    const first = sorted[0];
    const lastPt = sorted[sorted.length - 1];
    return {
      label: s.contract.label,
      strike: s.contract.strike,
      expiry: s.contract.expiry,
      side: s.contract.optionSide,
      point_count: pts.length,
      min: values.length ? Math.min(...values) : 0,
      max: values.length ? Math.max(...values) : 0,
      last: lastPt?.value ?? 0,
      trend: seriesTrend(values),
      time_range_et: {
        start: first ? formatMarketDateTime(first.tsMs) : '',
        end: lastPt ? formatMarketDateTime(lastPt.tsMs) : '',
      },
      samples,
    };
  });

  return {
    symbol,
    metric,
    session_ymd: sessionYmd,
    available_sessions: availableSessions,
    contracts,
    loading,
  };
}

export function buildStableChartAiContext(opts: {
  contract: ContractHistoricalParams;
  activeTab: 'eod' | 'intraday';
  loading?: boolean;
  eod?: {
    dateRange: { from: string; to: string };
    primaryLabel: string;
    primaryChartData: Array<{ date: string; value: number }>;
  };
  intraday?: {
    sessionYmd: string;
    tradeCount: number;
    netSignedUsd: number;
    cumulativeSeries: Array<{ tickLabel?: string; xKey?: string; cumulative: number }>;
  };
}): StableChartAiContext | null {
  const { contract, activeTab, loading, eod, intraday } = opts;
  const ot = (contract.optionType || 'call').toString();
  const label = `${contract.underlying} $${contract.strike} ${ot.toUpperCase()} ${contract.expiry || ''}`.trim();

  const ctx: StableChartAiContext = {
    contract_label: label,
    underlying: String(contract.underlying),
    strike: Number(contract.strike),
    expiry: String(contract.expiry || '').slice(0, 10),
    option_type: ot,
    active_tab: activeTab,
    loading,
  };

  if (eod && eod.primaryChartData.length > 0) {
    const vals = eod.primaryChartData.map((p) => p.value);
    const samples = downsample(eod.primaryChartData, MAX_SAMPLE_POINTS);
    ctx.eod = {
      date_from: eod.dateRange.from,
      date_to: eod.dateRange.to,
      primary_metric: eod.primaryLabel,
      observations: eod.primaryChartData.length,
      min: Math.min(...vals),
      max: Math.max(...vals),
      last: vals[vals.length - 1],
      samples,
    };
  }

  if (intraday && intraday.cumulativeSeries.length > 0) {
    const samples = downsample(intraday.cumulativeSeries, MAX_SAMPLE_POINTS).map((p) => ({
      time_et: p.tickLabel || p.xKey || '',
      cumulative_usd: p.cumulative,
    }));
    ctx.intraday = {
      session_ymd: intraday.sessionYmd,
      trade_count: intraday.tradeCount,
      net_signed_usd: intraday.netSignedUsd,
      samples,
    };
  }

  if (!ctx.eod && !ctx.intraday && !loading) return null;
  return ctx;
}

/** Normalize QuestDB ts strings on series points (for AI + charts). */
export function normalizeRtlSeriesTimestamps(series: RtlContractSeries[]): RtlContractSeries[] {
  return series.map((s) => ({
    ...s,
    points: s.points.map((p) => {
      const tsMs = parseQuestDbTimestampMs(p.ts);
      return tsMs > 0 ? { ...p, tsMs, ts: p.ts || new Date(tsMs).toISOString() } : p;
    }),
  }));
}
